/**
 * Review Appeal Dialog
 * Allows contractors/verifiers/inspectors to file appeals against reviews
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider
} from '@mui/material';
import {
  Gavel,
  Close,
  Add,
  Delete,
  Info
} from '@mui/icons-material';
import reviewAppealService from '../../services/reviewAppealService';
import DialogCloseButton from '../common/DialogCloseButton';

const ReviewAppealDialog = ({ 
  open, 
  onClose, 
  review, 
  onAppealFiled 
}) => {
  const [appealReason, setAppealReason] = useState('');
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [newEvidence, setNewEvidence] = useState({ type: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const appealReasons = [
    { value: 'factually_incorrect', label: 'Factually Incorrect', description: 'The review contains factually inaccurate statements about my work' },
    { value: 'false_allegations', label: 'False Allegations', description: 'The review contains false information about my work' },
    { value: 'malicious_intent', label: 'Malicious Intent', description: 'The review was written with intent to harm my reputation' },
    { value: 'not_related_to_work', label: 'Not Related to Work', description: 'The review is not about the work I provided' },
    { value: 'breach_of_privacy', label: 'Breach of Privacy', description: 'The review discloses private or confidential information' },
    { value: 'inappropriate_language', label: 'Inappropriate Language', description: 'The review contains offensive or inappropriate language' },
    { value: 'fraudulent_review', label: 'Fraudulent Review', description: 'The reviewer was never my client or the review is fabricated' },
    { value: 'other', label: 'Other', description: 'Other reason not listed above' }
  ];

  const evidenceTypes = [
    { value: 'contract', label: 'Contract/Agreement' },
    { value: 'communication_log', label: 'Messages/Communication' },
    { value: 'photo', label: 'Photos/Documentation' },
    { value: 'video', label: 'Video Evidence' },
    { value: 'document', label: 'Document' },
    { value: 'payment_proof', label: 'Payment Proof' },
    { value: 'completion_certificate', label: 'Completion Certificate' },
    { value: 'witness_statement', label: 'Witness Statement' },
    { value: 'other', label: 'Other' }
  ];

  const addEvidence = () => {
    if (newEvidence.type && newEvidence.description) {
      setEvidence([...evidence, newEvidence]);
      setNewEvidence({ type: '', description: '' });
    }
  };

  const removeEvidence = (index) => {
    setEvidence(evidence.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!appealReason) {
      setError('Please select a reason for your appeal');
      return;
    }

    if (!description.trim() || description.trim().length < 20) {
      setError('Please provide a detailed description (at least 20 characters)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const appealData = {
        reviewId: review.id,
        reviewType: review.revieweeRole === 'verifier' ? 'verifier_review' : 'user_review',
        appealReason: appealReason,
        appealDescription: description.trim(),
        supportingEvidence: evidence
      };

      const result = await reviewAppealService.fileAppeal(appealData);

      if (result.success) {
        setSuccess('Appeal filed successfully! Our team will review it within 72 hours.');
        
        // Clear form
        setAppealReason('');
        setDescription('');
        setEvidence([]);
        
        // Notify parent
        if (onAppealFiled) {
          onAppealFiled(result.data);
        }
        
        // Close after delay
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error filing appeal:', error);
      setError(error.message || 'Failed to file appeal');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAppealReason('');
      setDescription('');
      setEvidence([]);
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!review) {
    return null;
  }

  const selectedReason = appealReasons.find(r => r.value === appealReason);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Appeal Review
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Review Being Appealed */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Review Being Appealed:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip 
              label={`${review.rating}/5`} 
              size="small" 
              color={review.rating >= 4 ? 'success' : review.rating >= 3 ? 'warning' : 'error'}
            />
            <Typography variant="body2" color="text.secondary">
              by {review.reviewer_first_name} {review.reviewer_last_name}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            fontStyle: 'italic',
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            "{review.review_text || review.comment}"
          </Typography>
        </Box>

        {/* Appeal Reason */}
        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2 }}>
            Reason for Appeal *
          </FormLabel>
          <RadioGroup
            value={appealReason}
            onChange={(e) => {
              setAppealReason(e.target.value);
              setError('');
            }}
          >
            {appealReasons.map((reason) => (
              <Box
                key={reason.value}
                sx={{
                  border: '1px solid',
                  borderColor: appealReason === reason.value ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: appealReason === reason.value ? 'primary.lighter' : 'transparent'
                }}
              >
                <FormControlLabel
                  value={reason.value}
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {reason.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reason.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    m: 0, 
                    p: 2,
                    width: '100%'
                  }}
                />
              </Box>
            ))}
          </RadioGroup>
          {selectedReason && (
            <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Your appeal will be reviewed by our moderation team within 72 hours.
              </Typography>
            </Alert>
          )}
        </FormControl>

        {/* Detailed Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Detailed Explanation *
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide a detailed explanation of why you believe this review should be reconsidered.
            Include specific facts and circumstances.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Example: This review contains false information. I completed all work according to the contract dated [date]. The client signed off on the completion certificate on [date]. I have documentation proving that all deliverables were met and the client expressed satisfaction at the time..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (error && e.target.value.trim().length >= 20) {
                setError('');
              }
            }}
            variant="outlined"
            disabled={loading}
            helperText={`${description.length}/1000 characters (minimum 20 required)`}
            inputProps={{ maxLength: 1000 }}
          />
        </Box>

        {/* Supporting Evidence */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Supporting Evidence (Optional but Recommended)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            List any evidence you have to support your appeal. You can upload actual files later.
          </Typography>

          {/* Existing Evidence List */}
          {evidence.length > 0 && (
            <List sx={{ mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {evidence.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        onClick={() => removeEvidence(index)}
                        disabled={loading}
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Chip 
                          label={evidenceTypes.find(t => t.value === item.type)?.label || item.type}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      }
                      secondary={item.description}
                    />
                  </ListItem>
                  {index < evidence.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Add Evidence Form */}
          <Box sx={{ 
            p: 2, 
            border: '1px solid', 
            borderColor: 'grey.300', 
            borderRadius: 1
          }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Evidence Type</FormLabel>
              <TextField
                select
                fullWidth
                value={newEvidence.type}
                onChange={(e) => setNewEvidence({ ...newEvidence, type: e.target.value })}
                disabled={loading}
                SelectProps={{
                  native: true
                }}
              >
                <option value="">Select type...</option>
                {evidenceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </TextField>
            </FormControl>

            <TextField
              fullWidth
              label="Evidence Description"
              placeholder="Describe this piece of evidence..."
              value={newEvidence.description}
              onChange={(e) => setNewEvidence({ ...newEvidence, description: e.target.value })}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <Button
              startIcon={<Add />}
              onClick={addEvidence}
              disabled={!newEvidence.type || !newEvidence.description || loading}
              variant="outlined"
              fullWidth
            >
              Add Evidence
            </Button>
          </Box>
        </Box>

        {/* Important Information */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Please Note:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0, mt: 1 }}>
            <li>Filing an appeal does not automatically remove the review</li>
            <li>Our team will review your appeal within 72 hours</li>
            <li>You will be notified of the decision via email</li>
            <li>False or frivolous appeals may result in penalties</li>
          </Typography>
        </Alert>

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
              Filing your appeal...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !appealReason || description.trim().length < 20}
          variant="contained"
          startIcon={<Gavel />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Filing...' : 'File Appeal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewAppealDialog;

